import React, { useContext } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ThemeContext from '../../utils/theming';

import { Container } from '../../containers/DefaultStyles/Details';
import {
    Body,
    Content,
    Footer,
    PeopleSigned,
    Details,
    DetailsContainer,
    Title
} from '../../containers/DefaultStyles/Shared';

import WidgetHeader from '../Widget/WidgetHeader';
import Overlay from '../Widget/Overlay';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayPartner from '../Widget/OverlayPartner';

import LinkButton from '../../components/Link/LinkButton';
import ShareDrawer from '../../components/ShareDrawer';
import { BackArrow } from '../../components/Shared/icons';

import { onCompleteTweet } from './actions';
import {
    setOverlayPartnerVisible,
    onGoToNextCard,
    onBackWithinCard,
    setExitingCardProps,
    getExitingCardProps
} from '../Widget/actions';

import { isLongText } from '../../components/Shared/commonFunctions';
import { getActionCompletedTime } from '../../utils/actionTiming';
import { ActionTypes } from '../../utils/enums';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { formatThousands } from '../../utils/fillDynamicText';
import getTwitterShareUrl from '../../utils/tweet/tweetShare';
import {
    relevantRepresentatives,
    joinEnglish
} from '../../utils/localRepresentative/parser';
import { copyFor, capitalization } from '../../copy/copy';

const LinkText = styled.div`
    font-size: 16px;
    line-height: 24px;
    color: #333;
    font-weight: normal;
    margin-top: 12px;
    font-family: Lato;

    span {
        font-weight: 800;
        color: ${props => props.theme.colors.primary};
        cursor: pointer;
    }
`;

const ButtonRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    width: calc(100% - 32px);
    margin: 0 auto;
`;

const Back = styled.span`
    cursor: pointer;
    margin-top: 10px;
    font-size: 10px;
    font-weight: 500;
    color: #008eff;
`;

const TweetDetails = props => (
    <Container>
        <Overlay />
        <OverlayPublisher />
        <OverlayPartner transitionType="right-to-left" />
        <Body>
            <WidgetHeader />
            <Content>
                <Title
                    isLocalRepLoading={false}
                    isLongText={isLongText(props.feedbackTitle, 40)}
                    isExtraLongText={isLongText(props.feedbackTitle, 95)}
                    onClick={e => props.setOverlayPartnerVisible(true, e, false)}
                    dangerouslySetInnerHTML={{
                        __html: props.feedbackTitle
                    }}
                />
                <DetailsContainer>
                    <Details>
                        <LinkText
                            theme={useContext(ThemeContext)}
                            dangerouslySetInnerHTML={{ __html: props.feedbackText }}
                            onClick={e => props.setOverlayPartnerVisible(true, e, false)}
                        />
                        <PeopleSigned>
                            {props.peopleCount}{' '}
                            {copyFor(
                                'miscellaneous',
                                'numAnswered',
                                'tweet',
                                capitalization.all
                            )}
                        </PeopleSigned>
                        <Back
                            onClick={() => {
                                props.setExitingCardProps(
                                    'tweet',
                                    props,
                                    props.activeAction
                                );

                                props.onBackWithinCard(
                                    props.actionIdentifier,
                                    props.card,
                                    true,
                                    false
                                );
                            }}
                        >
                            <BackArrow width={8} height={8} />{' '}
                            {copyFor('buttons', 'back', 'tweet', capitalization.all)}
                        </Back>
                    </Details>
                </DetailsContainer>
            </Content>
        </Body>
        <ButtonRow>
            <LinkButton
                label={props.buttonText}
                onClick={() => {
                    props.setExitingCardProps('tweet', props, props.activeAction);

                    props.onCompleteTweet(
                        props.apiRequestData,
                        props.actionCompletedTime(),
                        props.actionIdentifier,
                        props.feedbackText,
                        props.feedbackTitle,
                        props.useLocalRep,
                        props.localRepresentativeState
                    );
                    window.open(
                        getTwitterShareUrl(
                            props.activeAction.textOriginal,
                            props.localRepTwitterMention,
                            props.localRepPostalCode,
                            props.partnerName,
                            props.publisherName,
                            props.articleUrl,
                            null,
                            props.repDistrict,
                            props.repState
                        ),
                        'popup',
                        'height=350,width=600'
                    );

                    if (props.nextAction) {
                        props.onGoToNextCard(
                            props.nextAction.identifier,
                            props.nextCard.type,
                            props.nextAction.type,
                            props.nextAction.skipFrontCard ||
                                props.nextAction.scrollableDesign,
                            false
                        );
                    }
                }}
            />
        </ButtonRow>
        <Footer>
            <ShareDrawer
                beforeBack={() => {
                    props.setExitingCardProps('tweet', props, props.activeAction);
                }}
                backgroundColor={'#fff'}
            />
        </Footer>
    </Container>
);

TweetDetails.propTypes = {
    actionCompletedTime: PropTypes.func.isRequired,
    actionIdentifier: PropTypes.string.isRequired,
    activeAction: PropTypes.object.isRequired,
    apiRequestData: PropTypes.object.isRequired,
    articleUrl: PropTypes.string,
    buttonText: PropTypes.string,
    card: PropTypes.object,
    feedbackText: PropTypes.string.isRequired,
    feedbackTitle: PropTypes.string,
    localRepPostalCode: PropTypes.string,
    localRepresentativeState: PropTypes.number,
    localRepTwitterMention: PropTypes.string,
    nextAction: PropTypes.object,
    nextCard: PropTypes.object,
    onBackWithinCard: PropTypes.func.isRequired,
    onCompleteTweet: PropTypes.func.isRequired,
    onGoToNextCard: PropTypes.func.isRequired,
    partnerName: PropTypes.string,
    peopleCount: PropTypes.string.isRequired,
    publisherName: PropTypes.string,
    repDistrict: PropTypes.string,
    repState: PropTypes.string,
    setExitingCardProps: PropTypes.func.isRequired,
    setOverlayPartnerVisible: PropTypes.func.isRequired,
    useLocalRep: PropTypes.bool
};

export default connect(
    (state, props) => {
        const exitingProps = getExitingCardProps(
            'tweet',
            ['/tweet'],
            props.location.pathname,
            state
        );
        if (exitingProps) return exitingProps;

        const nextAction = getNextAction(
            state.widget.actions,
            state.widget.activeAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );

        const reps = state.localRep.localRepData
            ? relevantRepresentatives(
                  state.widget.activeAction.representativeSearch,
                  state.localRep.localRepData.representative
              ).slice(0, 3)
            : [];
        const localRepData = state.localRep.localRepData || {};
        const repDistrict = localRepData.district || 'your district';
        const repState = localRepData.stateName || 'your state';
        return {
            repDistrict,
            repState,
            activeAction: state.widget.activeAction,
            card: state.widget.cards[state.widget.activeAction.order - 1],
            nextAction,
            nextCard: nextAction ? state.widget.cards[nextAction.order - 1] : null,
            peopleCount: formatThousands(state.widget.activeAction.noOfAnswers) || 121,
            feedbackText:
                state.widget.activeAction.thankYouText ||
                copyFor('defaultText', 'subheading', 'tweet'),
            feedbackTitle:
                state.widget.activeAction.thankYouTitle ||
                copyFor('defaultText', 'heading', 'tweet'),
            buttonText:
                state.widget.activeAction.customButtonText ||
                copyFor('buttons', 'start', 'tweet'),
            actionCompletedTime: getActionCompletedTime(state, 'tweet'),
            apiRequestData: state.widget.apiRequestData,
            useLocalRep: !!state.widget.activeAction.representativeSearch,
            actionIdentifier: state.widget.activeAction.identifier,
            localRepresentativeState: state.widget.activeAction.localRepresentativeState,
            localRepPostalCode: (state.localRep.localRepData.address || {}).zipCode,
            localRepTwitterMention: joinEnglish(
                reps.map(rep => {
                    if (rep.twitterMention && rep.twitterMention.length > 0) {
                        return '@' + rep.twitterMention;
                    }
                    return rep.fullName;
                })
            ),
            partnerName:
                state.widget.activeAction.partner !== undefined
                    ? state.widget.activeAction.partner.twitterMention !== undefined
                        ? '@' + state.widget.activeAction.partner.twitterMention
                        : state.widget.activeAction.partner.name
                    : 'Speakable',
            publisherName:
                state.widget.twitterMention !== undefined
                    ? '@' + state.widget.twitterMention
                    : state.widget.publisherName,
            articleUrl: state.widget.articleUrl
        };
    },
    {
        onCompleteTweet,
        setOverlayPartnerVisible,
        onGoToNextCard,
        onBackWithinCard,
        setExitingCardProps
    },
    null,
    {
        areStatesEqual: (next, prev) => {
            if (next.widget.activeAction.type !== ActionTypes.Tweet) return true;

            return next === prev;
        }
    }
)(TweetDetails);
